import React, { useState, useEffect, useMemo } from "react";
import { pgLibraryApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./PGLibrary.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const defaultInfo = {
  Page_Title: "",
  Subtitle: "",
  Description: "",
  Total_Books: "",
  Total_Journals: "",
  Seating_Capacity: "",
  Established_Year: "",
  Library_Area: "",
  Internet_Facility: "Available",
  Librarian_Name: "",
  Location: "",
  Librarian_Email: "",
  Librarian_Phone: "",
  OPAC_Link: ""
};

const PGLibrary = () => {
  const [formData, setFormData] = useState(defaultInfo);
  const [loading, setLoading]   = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`${pgLibraryApi}/page-info`);
      const result = await response.json();
      if (response.ok && result.data) {
        setFormData(result.data);
      }
    } catch (error) {
      console.error("Error fetching PG Library data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiClient(`${pgLibraryApi}/page-info`, {
        method: "POST",
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setIsEditing(false);
        fetchData();
      }
    } catch (error) {
      console.error("Error saving library info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Derived Stats ──
  const stats = useMemo(() => [
    { label: "Research Books", val: formData.Total_Books || "0+", icon: "📚" },
    { label: "PG Journals", val: formData.Total_Journals || "0+", icon: "📓" },
    { label: "Research Cabin", val: formData.Seating_Capacity || "0", icon: "🏢" },
    { label: "Est. Year", val: formData.Established_Year || "—", icon: "🏺" }
  ], [formData]);

  return (
    <div className="pgl-page">
      {/* ── Header ── */}
      <div className="pgl-header">
        <div className="pgl-header-left">
          <div className="pgl-header-icon">🎓</div>
          <div>
            <div className="pgl-breadcrumb">
              <span>Library</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="pgl-bc-active">PG Library</span>
            </div>
            <h1>{formData.Page_Title || "P.G. Library Management"}</h1>
            <p>{formData.Subtitle || "Advanced academic resources for post-graduate students and researchers."}</p>
          </div>
        </div>
        <div className="pgl-header-actions">
           <button className="pgl-btn-primary" onClick={() => setIsEditing(true)}>
             <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" style={{marginRight:6}}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4L18.5 2.5z" /></svg>
             Edit Details
           </button>
        </div>
      </div>

      <div className="pgl-tabs">
        <button className={`pgl-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={`pgl-tab ${activeTab === 'librarian' ? 'active' : ''}`} onClick={() => setActiveTab('librarian')}>Admin & Contact</button>
        <button className={`pgl-tab ${activeTab === 'research' ? 'active' : ''}`} onClick={() => setActiveTab('research')}>Research Tools</button>
      </div>

      {loading && <div className="loading-overlay">Syncing PG records...</div>}

      <div className="pgl-tab-content">
        {activeTab === 'overview' && (
          <>
            <div className="pgl-preview-banner">
              <div className="ppb-left">
                <span className="ppb-badge">Post Graduate Wing</span>
                <h2>Research Collections</h2>
                <p className="ppb-desc">{formData.Description || "A specialized wing catering to the advanced research needs of post-graduate scholars and faculty members."}</p>
                <div className="ppb-contact-row">
                  <span>📍 {formData.Location || "Main Library, 1st Floor"}</span>
                  <span>📏 {formData.Library_Area || "3500 Sq. Ft."}</span>
                  <span>📶 WiFi: {formData.Internet_Facility || "High-Speed Available"}</span>
                </div>
              </div>
              <div className="ppb-right">
                <div className="ppb-stats-grid">
                  <div><strong>{formData.Total_Books || "0"}</strong><span>PG Books</span></div>
                  <div><strong>{formData.Total_Journals || "0"}</strong><span>Journals</span></div>
                </div>
              </div>
            </div>

            <div className="pgl-overview-stats">
              {stats.map((s, i) => (
                <div key={i} className="pgl-stat-card">
                  <span className="pgl-stat-icon">{s.icon}</span>
                  <div>
                    <strong>{s.val}</strong>
                    <p>{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pgl-notice-box">
              <span>🧪</span>
              <div>
                <strong>Researcher Privileges</strong>
                <p>PG students and research scholars are entitled to extended borrowing periods and access to digital research journals like JSTOR and Elsevier.</p>
              </div>
            </div>
          </>
        )}

        {activeTab === 'librarian' && (
          <div className="pgl-info-row">
            <div className="pgl-info-box">
              <h3>Library Administration</h3>
              <div className="pgl-info-list">
                <div className="pgl-info-row-item"><span>🏛️</span><p><strong>Head Librarian:</strong> {formData.Librarian_Name || "—"}</p></div>
                <div className="pgl-info-row-item"><span>📧</span><p><strong>Official Email:</strong> {formData.Librarian_Email || "—"}</p></div>
                <div className="pgl-info-row-item"><span>📞</span><p><strong>Extension:</strong> {formData.Librarian_Phone || "—"}</p></div>
              </div>
            </div>
            <div className="pgl-info-box">
              <h3>Physical Presence</h3>
              <div className="pgl-info-list">
                <div className="pgl-info-row-item"><span>📍</span><p><strong>Wing:</strong> {formData.Location || "PG Block"}</p></div>
                <div className="pgl-info-row-item"><span>⏰</span><p><strong>Hours:</strong> Mon - Sat (08:30 AM - 06:00 PM)</p></div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'research' && (
          <div className="pgl-info-box">
             <h3>Digital Archive Access</h3>
             <div className="pgl-link-list">
               <div className="pgl-link-item">
                 <span>🧬</span>
                 <div>
                   <strong>OPAC & Research Gateway</strong>
                   <p>{formData.OPAC_Link || "Access international journals and digital manuscripts via our dedicated research portal."}</p>
                 </div>
               </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Edit Modal ── */}
      {isEditing && (
        <div className="pgl-overlay" onClick={() => setIsEditing(false)}>
          <div className="pgl-form-modal" onClick={e => e.stopPropagation()}>
            <div className="pgl-modal-head">
              <div>
                <h2>Edit PG Library Details</h2>
                <p>Update statistical data and research facility info.</p>
              </div>
              <button className="pgl-modal-close" onClick={() => setIsEditing(false)}>×</button>
            </div>
            <form className="pgl-form" onSubmit={handleSave}>
              <div className="pgl-field full"><label>Page Title</label><input name="Page_Title" value={formData.Page_Title || ""} onChange={handleChange} required /></div>
              <div className="pgl-field full"><label>Subtitle</label><input name="Subtitle" value={formData.Subtitle || ""} onChange={handleChange} /></div>
              <div className="pgl-field full"><label>Description</label><textarea name="Description" value={formData.Description || ""} onChange={handleChange} rows="3" /></div>
              
              <div className="pgl-field-row">
                <div className="pgl-field"><label>Total Books</label><input name="Total_Books" value={formData.Total_Books || ""} onChange={handleChange} /></div>
                <div className="pgl-field"><label>Total Journals</label><input name="Total_Journals" value={formData.Total_Journals || ""} onChange={handleChange} /></div>
              </div>
              
              <div className="pgl-field-row">
                <div className="pgl-field"><label>Seating Capacity</label><input name="Seating_Capacity" value={formData.Seating_Capacity || ""} onChange={handleChange} /></div>
                <div className="pgl-field"><label>Established Year</label><input name="Established_Year" value={formData.Established_Year || ""} onChange={handleChange} /></div>
              </div>

              <div className="pgl-field-row">
                <div className="pgl-field"><label>Library Area (Sq Ft)</label><input name="Library_Area" value={formData.Library_Area || ""} onChange={handleChange} /></div>
                <div className="pgl-field"><label>Internet Facility</label><input name="Internet_Facility" value={formData.Internet_Facility || ""} onChange={handleChange} /></div>
              </div>

              <div className="pgl-field full"><label>Librarian Name</label><input name="Librarian_Name" value={formData.Librarian_Name || ""} onChange={handleChange} /></div>
              
              <div className="pgl-field-row">
                <div className="pgl-field"><label>Librarian Email</label><input type="email" name="Librarian_Email" value={formData.Librarian_Email || ""} onChange={handleChange} /></div>
                <div className="pgl-field"><label>Librarian Phone</label><input name="Librarian_Phone" value={formData.Librarian_Phone || ""} onChange={handleChange} /></div>
              </div>

              <div className="pgl-field full"><label>Physical Location</label><input name="Location" value={formData.Location || ""} onChange={handleChange} /></div>
              <div className="pgl-field full"><label>OPAC Link (Optional)</label><input name="OPAC_Link" value={formData.OPAC_Link || ""} onChange={handleChange} /></div>

              <div className="pgl-form-footer">
                <button type="button" className="pgl-btn-light" onClick={() => setIsEditing(false)}>Cancel</button>
                <button type="submit" className="pgl-btn-primary">Save PG Library Data</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PGLibrary;